﻿import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DocumentEditor from '../../components/DocumentEditor';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Document, DocumentContent } from '../../types';
import { FaFileAlt } from 'react-icons/fa';

import {
        FiFile,
        FiPrinter,
        FiShare2,
        FiDownload,
        FiLock,
        FiUnlock,
        FiEdit,
        FiX,
        FiPlus,
        FiUpload
} from 'react-icons/fi';

interface Patient {
        id: number;
        first_name: string;
        last_name: string;
        date_of_birth?: string;
        medical_record_number?: string;
}

interface DocumentItemProps {
        document: Document;
        isSelected: boolean;
        onClick: () => void;
        onEdit: () => void;
        onToggleLock: () => void;
        isLocked: boolean;
        isEditing: boolean;
}

const DocumentItem: React.FC<DocumentItemProps> = ({ 
        document, 
        isSelected, 
        onClick, 
        onEdit,
        onToggleLock,
        isLocked
}) => {
        const formatDate = (dateString: string) => {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                });
        };

        return (
                <div className={`flex items-start space-x-2 p-3 hover:bg-base-200 cursor-pointer ${
                                isSelected ? 'bg-primary/10' : ''
                        }`}
                        onClick={onClick}
                >
                        <FiFile className="mt-1 opacity-50" />
                        <div className="flex-1">
                                <div className="text-sm font-medium">{document.title}</div>
                                <div className="text-xs opacity-60">
                                        {formatDate(document.document_date)}
                                </div>
                                {document.dictation_reference && (
                                        <div className="text-xs opacity-40 mt-1">
                                                Generated from Dictation: {document.dictation_reference}
                                        </div>
                                )}
                                <div className="flex space-x-2 mt-1">
                                        <button
                                                className="p-1 opacity-50 hover:opacity-100"
                                                onClick={(e) => {
                                                        e.stopPropagation();
                                                        onToggleLock();
                                                }}
                                                title={isLocked ? 'Unlock document' : 'Lock document'}
                                        >
                                                {isLocked ? (
                                                        <FiLock size={14} className="text-error" />
                                                ) : (
                                                        <FiUnlock size={14} className="text-success" />
                                                )}
                                        </button>
                                        <button
                                                className={`p-1 ${
                                                        isLocked 
                                                                ? 'opacity-30 cursor-not-allowed' 
                                                                : 'opacity-50 hover:opacity-100'
                                                }`}
                                                onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!isLocked) onEdit();
                                                }}
                                                disabled={isLocked}
                                                title={isLocked ? 'Document is locked' : 'Edit document'}
                                        >
                                                <FiEdit size={14} />
                                        </button>
                                        <button
                                                className="p-1 opacity-50 hover:opacity-100"
                                                onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Print', document.id);
                                                }}
                                                title="Print document"
                                        >
                                                <FiPrinter size={14} />
                                        </button>
                                        <button
                                                className="p-1 opacity-50 hover:opacity-100"
                                                onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Download', document.id);
                                                }}
                                                title="Download document"
                                        >
                                                <FiDownload size={14} />
                                        </button>
                                        <button
                                                className="p-1 opacity-50 hover:opacity-100"
                                                onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Share', document.id);
                                                }}
                                                title="Share document"
                                        >
                                                <FiShare2 size={14} />
                                        </button>
                                </div>
                        </div>
                </div>
        );
};

const Documents: React.FC = () => {
        const { token } = useAuth();
        const navigate = useNavigate();
        const location = useLocation();
        const editorRef = useRef<any>(null);
        const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
        const [documentContent, setDocumentContent] = useState<any>(null);
        const [documentContentFormat, setDocumentContentFormat] = useState<'sfdt' | 'text' | 'html' | null>(null);
        const [documents, setDocuments] = useState<Document[]>([]);
        const [loading, setLoading] = useState<boolean>(false);
        const [error, setError] = useState<string | null>(null);
        const [appHeight, setAppHeight] = useState('100vh');
        const [documentStates, setDocumentStates] = useState<Record<string, {
                isEditing: boolean;
                isLocked: boolean;
        }>>({});
        
        // Patient search states
        const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
        const [patientSearch, setPatientSearch] = useState('');
        const [patientResults, setPatientResults] = useState<Patient[]>([]);
        const [showPatientResults, setShowPatientResults] = useState(false);
        const [autoSelectDocumentId, setAutoSelectDocumentId] = useState<string | null>(null);
        
        // Direction filter state
        const [selectedDirection, setSelectedDirection] = useState<'all' | 'inbound' | 'outbound' | 'internal'>('all');
        
        // Upload modal state
        const [showUploadModal, setShowUploadModal] = useState(false);

        // Calculate app height
        useEffect(() => {
                const calculateHeight = () => {
                        const navbarHeight = 140;
                        setAppHeight(`calc(100vh - ${navbarHeight}px)`);
                };

                calculateHeight();
                window.addEventListener('resize', calculateHeight);
                return () => window.removeEventListener('resize', calculateHeight);
        }, []);

        // Patient search with debounce
        useEffect(() => {
                if (patientSearch.length >= 2 && token && !selectedPatient) {
                        const timer = setTimeout(async () => {
                                try {
                                        const response = await apiCall(`/patients/?search=${encodeURIComponent(patientSearch)}`, {
                                                method: 'GET',
                                        }, token);
                                        
                                        if (response.ok) {
                                                const data = await response.json();
                                                const patients = data.results || data || [];
                                                
                                                // Additional client-side filtering and scoring for relevance
                                                const searchLower = patientSearch.toLowerCase().trim();
                                                const filtered = patients
                                                        .map((patient: Patient) => {
                                                                const firstName = patient.first_name.toLowerCase();
                                                                const lastName = patient.last_name.toLowerCase();
                                                                const fullName = `${firstName} ${lastName}`;
                                                                const reverseName = `${lastName} ${firstName}`;
                                                                const mrn = (patient.medical_record_number || '').toLowerCase();
                                                                
                                                                // Calculate relevance score (higher is better)
                                                                let score = 0;
                                                                
                                                                // Exact match on first or last name (highest priority)
                                                                if (firstName === searchLower || lastName === searchLower) {
                                                                        score += 1000;
                                                                }
                                                                // Starts with search term (high priority)
                                                                else if (firstName.startsWith(searchLower) || lastName.startsWith(searchLower)) {
                                                                        score += 500;
                                                                }
                                                                // Full name exact match
                                                                else if (fullName === searchLower || reverseName === searchLower) {
                                                                        score += 800;
                                                                }
                                                                // Full name starts with
                                                                else if (fullName.startsWith(searchLower) || reverseName.startsWith(searchLower)) {
                                                                        score += 400;
                                                                }
                                                                // Contains in first or last name (lower priority)
                                                                else if (firstName.includes(searchLower) || lastName.includes(searchLower)) {
                                                                        score += 100;
                                                                }
                                                                // Contains in full name
                                                                else if (fullName.includes(searchLower) || reverseName.includes(searchLower)) {
                                                                        score += 50;
                                                                }
                                                                // MRN match
                                                                else if (mrn.includes(searchLower)) {
                                                                        score += 200;
                                                                }
                                                                
                                                                return { patient, score };
                                                        })
                                                .filter((item: { patient: any; score: number }) => item.score > 0)
                                                .sort((a: { patient: any; score: number }, b: { patient: any; score: number }) => b.score - a.score)
                                                .map((item: { patient: any; score: number }) => item.patient);
                                                setPatientResults(filtered);
                                                setShowPatientResults(filtered.length > 0);
                                        }
                                } catch (error) {
                                        console.error('Error searching patients:', error);
                                }
                        }, 300);
                        
                        return () => clearTimeout(timer);
                } else if (selectedPatient) {
                        // If a patient is selected, don't show results
                        setShowPatientResults(false);
                } else {
                        setPatientResults([]);
                        setShowPatientResults(false);
                }
        }, [patientSearch, token, selectedPatient]);
        
        // Handle patient and document from navigation state (e.g., from saved letter)
        useEffect(() => {
                const state = location.state as { patientId?: string; patientName?: string; patientDob?: string; documentId?: string } | null;
                console.log('[Documents] Navigation state:', state);
                
                if (state?.patientId && !selectedPatient) {
                        // Fetch patient details to populate filter
                        const fetchPatient = async () => {
                                try {
                                        console.log('[Documents] Fetching patient:', state.patientId);
                                        const response = await apiCall(
                                                `/patients/${state.patientId}/`,
                                                { method: 'GET' },
                                                token || undefined
                                        );
                                        if (response.ok) {
                                                const patient = await response.json();
                                                console.log('[Documents] Patient fetched:', patient);
                                                setSelectedPatient(patient);
                                                setPatientSearch(`${patient.first_name} ${patient.last_name}`);
                                                
                                                // If a documentId is provided, wait for documents to load then auto-select it
                                                if (state.documentId) {
                                                        console.log('[Documents] Will auto-select document:', state.documentId);
                                                        // Store the document ID to select after documents are fetched
                                                        setAutoSelectDocumentId(state.documentId);
                                                }
                                        }
                                } catch (error) {
                                        console.error('[Documents] Error fetching patient:', error);
                                }
                        };
                        fetchPatient();
                }
        }, [location.state, token]);
        
        // Fetch documents list - re-fetch when patient or direction changes
        useEffect(() => {
                fetchDocuments();
        }, [token, selectedPatient, selectedDirection]);

        const fetchDocuments = async () => {
                if (!token) return;
                
                // Don't fetch if no patient is selected
                if (!selectedPatient) {
                        setDocuments([]);
                        setDocumentStates({});
                        return;
                }
                
                setLoading(true);
                setError(null);
                
                try {
                        let url = ENDPOINTS.DOCUMENTS.LIST;
                        const params = new URLSearchParams();
                        
                        // Add patient filter if selected
                        if (selectedPatient) {
                                params.append('patient_id', selectedPatient.id.toString());
                                console.log('[Documents] Fetching documents for patient:', selectedPatient.id);
                        }
                        
                        // Add direction filter if not "all"
                        if (selectedDirection !== 'all') {
                                params.append('direction', selectedDirection);
                        }
                        
                        if (params.toString()) {
                                url += `?${params.toString()}`;
                        }
                        
                        console.log('[Documents] Fetching from URL:', url);
                        
                        const response = await apiCall(
                                url,
                                { method: 'GET' },
                                token
                        );
                        
                        console.log('[Documents] Response status:', response.status, response.ok);
                        
                        if (!response.ok) {
                                const errorText = await response.text();
                                console.error('[Documents] Error response:', errorText);
                                throw new Error(`Failed to fetch documents: ${response.status} ${errorText}`);
                        }
                        
                        const data = await response.json();
                        console.log('[Documents] Received data:', data);
                        console.log('[Documents] data.results:', data.results);
                        console.log('[Documents] Raw data keys:', Object.keys(data));
                        const documentsList = data.results || data;
                        console.log('[Documents] Document count:', Array.isArray(documentsList) ? documentsList.length : 'Not an array');
                        console.log('[Documents] documentsList:', documentsList);
                        setDocuments(Array.isArray(documentsList) ? documentsList : []);
                        
                        // Initialize document states
                        const states: Record<string, { isEditing: boolean; isLocked: boolean }> = {};
                        documentsList.forEach((doc: Document) => {
                                states[doc.id] = { isEditing: false, isLocked: true };
                        });
                        setDocumentStates(states);
                        
                        // Auto-select document if specified in navigation state
                        if (autoSelectDocumentId && Array.isArray(documentsList)) {
                                const docToSelect = documentsList.find((d: Document) => d.id === autoSelectDocumentId);
                                if (docToSelect) {
                                        console.log('[Documents] Auto-selecting document:', docToSelect.title);
                                        handleDocumentSelect(docToSelect);
                                        setAutoSelectDocumentId(null); // Clear after selection
                                }
                        }
                } catch (err) {
                        console.error('[Documents] Error fetching documents:', err);
                        setError('Failed to load documents');
                } finally {
                        setLoading(false);
                }
        };
        
        const handlePatientSelect = (patient: Patient) => {
                setSelectedPatient(patient);
                setPatientSearch(`${patient.first_name} ${patient.last_name}`);
                setShowPatientResults(false);
                setPatientResults([]); // Clear results to prevent re-display
        };
        
        const clearPatientFilter = () => {
                setSelectedPatient(null);
                setPatientSearch('');
                setPatientResults([]);
                setShowPatientResults(false);
        };
        
        const handleCreateDocument = () => {
                if (selectedPatient) {
                        navigate('/documents/new', { state: { patient: selectedPatient, patientId: selectedPatient.id } });
                }
        };
        
        const handleUpload = () => {
                if (selectedPatient) {
                        setShowUploadModal(true);
                }
        };

        // Load document content when selected
        const loadDocumentContent = async (document: Document) => {
                if (!token) return;
                
                setLoading(true);
                setError(null);
                
                try {
                        const response = await apiCall(
                                `${ENDPOINTS.DOCUMENTS.CONTENT}${document.id}/content/`,
                                { method: 'GET' },
                                token
                        );
                        
                        if (!response.ok) {
                                throw new Error('Failed to load document content');
                        }
                        
                        const contentData: DocumentContent = await response.json();
                        
                        // Store the content and format - Syncfusion SFDT, HTML, or plain text
                        setDocumentContent(contentData.content || '');
                        setDocumentContentFormat((contentData.format as 'sfdt' | 'text' | 'html') || 'text');
                        
                        console.log('[Documents] Loaded document format:', contentData.format);
                } catch (err) {
                        console.error('Error loading document content:', err);
                        setError('Failed to load document content');
                        setDocumentContent(null);
                        setDocumentContentFormat(null);
                } finally {
                        setLoading(false);
                }
        };

        const handleDocumentSelect = async (document: Document) => {
                setSelectedDocument(document);
                await loadDocumentContent(document);
        };

        const toggleEdit = (docId: string) => {
                const doc = documents.find(d => d.id === docId);
                if (!doc || documentStates[docId]?.isLocked) return;
                
                // Navigate to edit page with document ID
                navigate(`/documents/edit/${docId}`);
        };

        const toggleLock = (docId: string) => {
                setDocumentStates(prev => ({
                        ...prev,
                        [docId]: {
                                ...prev[docId],
                                isLocked: !prev[docId].isLocked,
                                isEditing: prev[docId].isLocked ? false : prev[docId].isEditing
                        }
                }));
        };

        const handleSave = async (blob: Blob) => {
                if (!selectedDocument || !token) return;
                
                try {
                        // TODO: Implement save functionality
                        // This would upload the modified document back to MinIO
                        console.log('Saving document:', selectedDocument.id, blob);
                } catch (err) {
                        console.error('Error saving document:', err);
                        setError('Failed to save document');
                }
        };

        return (
                <div style={{ height: appHeight }} className="flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="px-6 py-4 flex-shrink-0">
                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                        <FaFileAlt className="text-primary" />
                                        Documents
                                </h1>
                        </div>
                        {/* Top Filter Bar */}
                        <div className="px-6 pb-4 flex-shrink-0">
                                <div className="bg-base-200 rounded-lg p-4 flex items-center justify-between gap-6">
                                        {/* Left Section: Patient Search */}
                                        <div className="relative flex-1 max-w-md">
                                                <label className="text-xs opacity-70 mb-1 block">Search Patient:</label>
                                                {selectedPatient ? (
                                                        <div className="inline-flex items-center space-x-2 px-3 py-2 bg-primary text-primary-content border border-primary rounded">
                                                                <span>
                                                                        {selectedPatient.first_name} {selectedPatient.last_name}
                                                                </span>
                                                                <span className="text-xs opacity-70">
                                                                        (DOB: {selectedPatient.date_of_birth || 'N/A'})
                                                                </span>
                                                                <button
                                                                        onClick={clearPatientFilter}
                                                                        className="opacity-70 hover:opacity-100"
                                                                        title="Clear patient filter"
                                                                >
                                                                        <FiX className="w-4 h-4" />
                                                                </button>
                                                        </div>
                                                ) : (
                                                        <input
                                                                type="text"
                                                                value={patientSearch}
                                                                onChange={(e) => setPatientSearch(e.target.value)}
                                                                className="input input-bordered w-full"
                                                                placeholder="Search by name, MRN..."
                                                        />
                                                )}
                                                
                                                {/* Patient Search Results Dropdown */}
                                                {showPatientResults && patientResults.length > 0 && (
                                                        <div className="absolute top-full left-0 mt-1 w-full bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-60 overflow-y-auto z-50">
                                                                {patientResults.map((patient) => (
                                                                        <div
                                                                                key={patient.id}
                                                                                onClick={() => handlePatientSelect(patient)}
                                                                                className="px-3 py-2 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                                                        >
                                                                                <div className="font-medium">
                                                                                        {patient.first_name} {patient.last_name}
                                                                                </div>
                                                                                <div className="text-xs opacity-60 space-y-0.5">
                                                                                        <div>DOB: {patient.date_of_birth || 'N/A'}</div>
                                                                                        <div>MRN: {patient.medical_record_number || 'N/A'}</div>
                                                                                </div>
                                                                        </div>
                                                                ))}
                                                        </div>
                                                )}
                                        </div>
                                        
                                        {/* Center Section: Direction Filter */}
                                        <div className="flex-shrink-0">
                                                <label className="text-xs opacity-60 mb-1 block">Direction:</label>
                                                <div className="flex items-center space-x-4">
                                                        <label className="flex items-center space-x-2 cursor-pointer">
                                                                <input
                                                                        type="radio"
                                                                        name="direction"
                                                                        value="all"
                                                                        checked={selectedDirection === 'all'}
                                                                        onChange={(e) => setSelectedDirection(e.target.value as any)}
                                                                        className="radio radio-primary"
                                                                />
                                                                <span className="text-sm">All</span>
                                                        </label>
                                                        <label className="flex items-center space-x-2 cursor-pointer">
                                                                <input
                                                                        type="radio"
                                                                        name="direction"
                                                                        value="inbound"
                                                                        checked={selectedDirection === 'inbound'}
                                                                        onChange={(e) => setSelectedDirection(e.target.value as any)}
                                                                        className="radio radio-primary"
                                                                />
                                                                <span className="text-sm">Inbound</span>
                                                        </label>
                                                        <label className="flex items-center space-x-2 cursor-pointer">
                                                                <input
                                                                        type="radio"
                                                                        name="direction"
                                                                        value="outbound"
                                                                        checked={selectedDirection === 'outbound'}
                                                                        onChange={(e) => setSelectedDirection(e.target.value as any)}
                                                                        className="radio radio-primary"
                                                                />
                                                                <span className="text-sm">Outbound</span>
                                                        </label>
                                                        <label className="flex items-center space-x-2 cursor-pointer">
                                                                <input
                                                                        type="radio"
                                                                        name="direction"
                                                                        value="internal"
                                                                        checked={selectedDirection === 'internal'}
                                                                        onChange={(e) => setSelectedDirection(e.target.value as any)}
                                                                        className="radio radio-primary"
                                                                />
                                                                <span className="text-sm">Internal</span>
                                                        </label>
                                                </div>
                                        </div>
                                        
                                        {/* Right Section: Action Buttons */}
                                        <div className="flex items-end gap-2 ml-auto">
                                                <button
                                                        onClick={handleCreateDocument}
                                                        disabled={!selectedPatient}
                                                        className="btn btn-primary gap-2"
                                                        title={selectedPatient ? 'Create a new document' : 'Select a patient first'}
                                                >
                                                        <FiPlus className="w-4 h-4" />
                                                        Create Document
                                                </button>
                                                <button
                                                        onClick={handleUpload}
                                                        disabled={!selectedPatient}
                                                        className="btn btn-success gap-2"
                                                        title={selectedPatient ? 'Upload documents' : 'Select a patient first'}
                                                >
                                                        <FiUpload className="w-4 h-4" />
                                                        Upload
                                                </button>
                                        </div>
                                </div>
                        </div>
                        
                        {/* Main Content Area */}
                        <div className="flex-1 flex flex-row overflow-hidden">
                                {/* Left sidebar - Document list */}
                                <div className="w-80 flex-shrink-0 border-r border-base-300 bg-base-100">
                                        <div className="flex flex-col h-full">
                                                <div className="p-4 border-b border-base-300 bg-base-200">
                                                        <h2 className="text-lg font-semibold">
                                                                Documents
                                                                {selectedPatient && (
                                                                        <span className="text-sm font-normal opacity-60 ml-2">
                                                                                ({documents.length})
                                                                        </span>
                                                                )}
                                                        </h2>
                                                </div>
                                                <div className="flex-1 overflow-y-auto">
                                                {!selectedPatient ? (
                                                        <div className="p-4 text-center opacity-60">
                                                                <p className="mb-2">No patient selected</p>
                                                                <p className="text-xs opacity-70">
                                                                        Please select a patient to view their documents
                                                                </p>
                                                        </div>
                                                ) : loading && documents.length === 0 ? (
                                                        <div className="p-4 text-center opacity-60">
                                                                Loading documents...
                                                        </div>
                                                ) : error && documents.length === 0 ? (
                                                        <div className="p-4 text-center text-error">
                                                                {error}
                                                        </div>
                                                ) : documents.length === 0 ? (
                                                        <div className="p-4 text-center opacity-60">
                                                                <p className="mb-2">No documents found</p>
                                                                <p className="text-xs opacity-70">
                                                                        This patient has no documents yet
                                                                </p>
                                                        </div>
                                                ) : (
                                                        documents.map((doc) => (
                                                                <DocumentItem
                                                                        key={doc.id}
                                                                        document={doc}
                                                                        isSelected={selectedDocument?.id === doc.id}
                                                                        onClick={() => handleDocumentSelect(doc)}
                                                                        onEdit={() => toggleEdit(doc.id)}
                                                                        onToggleLock={() => toggleLock(doc.id)}
                                                                        isLocked={documentStates[doc.id]?.isLocked ?? true}
                                                                        isEditing={documentStates[doc.id]?.isEditing ?? false}
                                                                />
                                                        ))
                                                )}
                                                </div>
                                        </div>
                                </div>

                                {/* Document viewer/editor area */}
                                <div className="flex-1 overflow-hidden">
                                        {loading && selectedDocument ? (
                                                <div className="h-full flex items-center justify-center opacity-60">
                                                        Loading document...
                                                </div>
                                        ) : error && selectedDocument ? (
                                                <div className="h-full flex items-center justify-center text-error">
                                                        {error}
                                                </div>
                                        ) : selectedDocument && documentContent ? (
                                                <div className="relative h-full bg-gray-200">
                                                        {documentContentFormat === 'html' ? (
                                                                // HTML documents - render in print layout style
                                                                <div className="h-full overflow-auto p-8">
                                                                        <style>{`
                                                                                .medical-letter-print p {
                                                                                        margin-bottom: 1em;
                                                                                        margin-top: 0;
                                                                                }
                                                                                .medical-letter-print h3 {
                                                                                        margin-top: 1.5em;
                                                                                        margin-bottom: 0.5em;
                                                                                }
                                                                        `}</style>
                                                                        <div 
                                                                                className="max-w-[210mm] mx-auto bg-white shadow-lg medical-letter-print"
                                                                                style={{
                                                                                        minHeight: '297mm',
                                                                                        padding: '25mm 20mm',
                                                                                        color: '#000000',
                                                                                        fontSize: '11pt',
                                                                                        fontFamily: 'Arial, sans-serif',
                                                                                        lineHeight: '1.6'
                                                                                }}
                                                                                dangerouslySetInnerHTML={{ __html: documentContent }}
                                                                        />
                                                                </div>
                                                        ) : (
                                                                // SFDT/DOCX documents - use DocumentEditor
                                                                <DocumentEditor
                                                                        ref={editorRef}
                                                                        initialContent={documentContent}
                                                                        onSave={handleSave}
                                                                        isReadOnly={documentStates[selectedDocument.id]?.isLocked ?? true}
                                                                        enableToolbar={false}
                                                                        showPropertiesPane={false}
                                                                        height="calc(100vh - 240px)"
                                                                />
                                                        )}
                                                </div>
                                        ) : (
                                                <div className="h-full flex items-center justify-center opacity-60">
                                                        Select a document to view
                                                </div>
                                        )}
                                </div>
                        </div>
                        
                        {/* Upload Modal */}
                        {showUploadModal && selectedPatient && (
                                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                        <div className="bg-base-100 rounded-lg shadow-xl p-6 w-full max-w-md">
                                                <div className="flex justify-between items-center mb-4">
                                                        <h3 className="text-lg font-semibold">
                                                                Upload Documents
                                                        </h3>
                                                        <button
                                                                onClick={() => setShowUploadModal(false)}
                                                                className="opacity-60 hover:opacity-100"
                                                        >
                                                                <FiX className="w-5 h-5" />
                                                        </button>
                                                </div>
                                                
                                                <div className="mb-4">
                                                        <p className="text-sm opacity-80 mb-2">
                                                                Patient: <span className="font-medium">{selectedPatient.first_name} {selectedPatient.last_name}</span>
                                                                {selectedPatient.date_of_birth && (
                                                                        <span className="ml-2 text-xs opacity-70">
                                                                                (DOB: {selectedPatient.date_of_birth})
                                                                        </span>
                                                                )}
                                                        </p>
                                                </div>
                                                
                                                <div className="border-2 border-dashed border-base-300 rounded-lg p-8 text-center mb-4">
                                                        <FiUpload className="w-12 h-12 mx-auto opacity-40 mb-3" />
                                                        <p className="text-sm opacity-80 mb-2">
                                                                Drag and drop files here, or click to browse
                                                        </p>
                                                        <input
                                                                type="file"
                                                                multiple
                                                                className="hidden"
                                                                id="file-upload"
                                                                onChange={(e) => {
                                                                        const files = e.target.files;
                                                                        if (files) {
                                                                                console.log('Files selected:', files);
                                                                                // TODO: Implement file upload logic
                                                                        }
                                                                }}
                                                        />
                                                        <label
                                                                htmlFor="file-upload"
                                                                className="btn btn-primary"
                                                        >
                                                                Browse Files
                                                        </label>
                                                </div>
                                                
                                                <div className="text-xs opacity-60">
                                                        Supported formats: PDF, DOC, DOCX, JPG, PNG, TIFF
                                                </div>
                                                
                                                <div className="flex justify-end gap-2 mt-6">
                                                        <button
                                                                onClick={() => setShowUploadModal(false)}
                                                                className="btn btn-ghost"
                                                        >
                                                                Cancel
                                                        </button>
                                                        <button
                                                                onClick={() => {
                                                                        // TODO: Implement upload functionality
                                                                        console.log('Upload clicked');
                                                                        setShowUploadModal(false);
                                                                }}
                                                                className="btn btn-primary"
                                                        >
                                                                Upload
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default Documents;